import { supabase } from "@/lib/supabase/client";

export type DocumentType = "side_letter" | "lpa" | "sub_doc" | "amendment" | "other";
export type DocumentStatus =
  | "pending"
  | "parsing"
  | "extracting"
  | "extracted"
  | "failed"
  | "archived";

export interface DocumentRow {
  id: string;
  tenant_id: string;
  fund_id: string;
  lp_id: string | null;
  document_type: DocumentType;
  title: string;
  storage_path: string;
  file_hash: string;
  page_count: number | null;
  word_count: number | null;
  is_scanned: boolean;
  extracted_text: string | null;
  status: DocumentStatus;
  error_message: string | null;
  uploaded_by: string | null;
  executed_date: string | null;
  effective_date: string | null;
  created_at: string;
  updated_at: string;
}

const COLS =
  "id, tenant_id, fund_id, lp_id, document_type, title, storage_path, file_hash, page_count, word_count, is_scanned, extracted_text, status, error_message, uploaded_by, executed_date, effective_date, created_at, updated_at";

/** List view skips extracted_text (can be hundreds of KB per row). */
const LIST_COLS = COLS.replace("extracted_text, ", "");

export type DocumentListRow = Omit<DocumentRow, "extracted_text">;

export async function listDocuments(
  tenantId: string,
  opts: { fundId?: string } = {},
): Promise<DocumentListRow[]> {
  let q = supabase
    .from("documents")
    .select(LIST_COLS)
    .eq("tenant_id", tenantId)
    .order("created_at", { ascending: false });
  if (opts.fundId) q = q.eq("fund_id", opts.fundId);
  const { data, error } = await q;
  if (error) throw new Error(`listDocuments: ${error.message}`);
  return (data ?? []) as unknown as DocumentListRow[];
}

export async function getDocument(id: string): Promise<DocumentRow> {
  const { data, error } = await supabase.from("documents").select(COLS).eq("id", id).single();
  if (error) throw new Error(`getDocument: ${error.message}`);
  return data;
}

export interface CreateDocumentInput {
  tenant_id: string;
  fund_id: string;
  lp_id?: string | null;
  document_type: DocumentType;
  title: string;
  storage_path: string;
  file_hash: string;
  page_count?: number | null;
  word_count?: number | null;
  extracted_text?: string | null;
  executed_date?: string | null;
  uploaded_by?: string | null;
}

export async function createDocument(input: CreateDocumentInput): Promise<DocumentRow> {
  const { data, error } = await supabase.from("documents").insert(input).select(COLS).single();
  if (error) {
    if (error.code === "23505") {
      throw new Error(
        "This exact file has already been uploaded to this tenant (matching content hash).",
      );
    }
    throw new Error(`createDocument: ${error.message}`);
  }
  return data;
}

export async function updateDocument(
  id: string,
  patch: Partial<
    Pick<DocumentRow, "status" | "error_message" | "title" | "executed_date" | "lp_id">
  >,
): Promise<void> {
  const { error } = await supabase.from("documents").update(patch).eq("id", id);
  if (error) throw new Error(`updateDocument: ${error.message}`);
}

export async function deleteDocument(id: string): Promise<void> {
  const { error } = await supabase.from("documents").delete().eq("id", id);
  if (error) throw new Error(`deleteDocument: ${error.message}`);
}

/** Storage path convention (RLS depends on it): `<tenant_id>/<uuid>/<filename>`. */
export async function uploadDocumentFile(
  tenantId: string,
  file: File,
): Promise<{ storagePath: string }> {
  const key = `${tenantId}/${crypto.randomUUID()}/${file.name}`;
  const { error } = await supabase.storage.from("documents").upload(key, file, {
    contentType: file.type || "application/octet-stream",
    upsert: false,
  });
  if (error) throw new Error(`uploadDocumentFile: ${error.message}`);
  return { storagePath: key };
}

/** Signed URL with short expiry — build doc §11.3. */
export async function getDocumentDownloadUrl(storagePath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from("documents")
    .createSignedUrl(storagePath, 300);
  if (error) throw new Error(`getDocumentDownloadUrl: ${error.message}`);
  return data.signedUrl;
}
